import BrandsInfiniteSelect from '@/components/_common/BrandsInfiniteSelect';
import ActiveExtraFilter from '@/components/_common/ExtraFilters/ActiveExtraFilter';
import GroupsInfiniteSelectField from '@/components/_common/GroupsInfiniteSelectField';
import ModelsInfiniteSelect from '@/components/_common/ModelsInfiniteSelect';
import { useMatrix } from '@/core/domain/enterprise/Matrix/Matrix.hooks';
import { usePartConfigurationProfile } from '@/core/domain/stock/Configuration/Configuration.hooks';
import { PartItemFieldsEnum } from '@/core/domain/stock/Configuration/Configuration.types';
import {
  FilterTypeEnum,
  ListStockItemsQuery,
} from '@/core/domain/stock/StockItems/StockItems.types';
import { ExtraFiltersProps } from '@/types/ExtraFiltersProps';
import { Checkbox, Col, Input, Row, Select } from 'antd';
import { useState } from 'react';
import { useIntl } from 'react-intl';

interface Props {
  setQueryParams: React.Dispatch<React.SetStateAction<ListStockItemsQuery>>;
  hiddenFilterType?: boolean;
  showVehicleFields?: boolean;
  showFilterPartReplacement?: boolean;
}

export default function SearchPartsExtraFilters({
  queryParams,
  setQueryParams,
  hiddenFilterType = false,
  showVehicleFields = true,
  showFilterPartReplacement = false,
}: Props & ExtraFiltersProps<ListStockItemsQuery>) {
  const intl = useIntl();

  const [brandIdSelected, setBrandIdSelected] = useState();

  const { data: matrixSettings } = useMatrix();

  const { data } = usePartConfigurationProfile();

  const showField = (field: PartItemFieldsEnum) => {
    return data?.result.find((profile) => profile.field === field)?.active;
  };

  return (
    <Row gutter={[16, 16]}>
      {!hiddenFilterType && (
        <Col span={24} md={12} lg={6}>
          <Select
            defaultValue={FilterTypeEnum.free}
            style={{ width: '100%' }}
            options={[
              {
                label: 'Por campo - Livre',
                value: FilterTypeEnum.free,
              },
              {
                label: 'Por campo - Especifico',
                value: FilterTypeEnum.specific,
              },
            ]}
            onChange={(value) =>
              setQueryParams((prev) => ({
                ...prev,
                filterType: value,
              }))
            }
          />
        </Col>
      )}

      {showField(PartItemFieldsEnum.BarCode) && (
        <Col span={24} sm={12} lg={6}>
          <Input
            value={queryParams?.barcode}
            onChange={({ target: { value } }) =>
              setQueryParams((prev) => ({ ...prev, barcode: value }))
            }
            placeholder={intl.formatMessage({ id: 'parts.entity.barCode' })}
          />
        </Col>
      )}

      {showField(PartItemFieldsEnum.OriginalCode) && (
        <Col span={24} sm={12} lg={6}>
          <Input
            value={queryParams?.originalCode}
            onChange={({ target: { value } }) =>
              setQueryParams((prev) => ({ ...prev, sku: value }))
            }
            placeholder={intl.formatMessage({
              id: 'parts.entity.originalCode',
            })}
          />
        </Col>
      )}

      {showField(PartItemFieldsEnum.ManufacturingCode) && (
        <Col span={24} sm={12} lg={6}>
          <Input
            value={queryParams?.manufacturingCode}
            onChange={({ target: { value } }) =>
              setQueryParams((prev) => ({ ...prev, manufacturingCode: value }))
            }
            placeholder={intl.formatMessage({
              id: 'parts.entity.manufacturerCode',
            })}
          />
        </Col>
      )}

      {showField(PartItemFieldsEnum.companyCode) && (
        <Col span={24} sm={12} lg={6}>
          <Input
            value={queryParams?.companyCode}
            onChange={({ target: { value } }) =>
              setQueryParams((prev) => ({ ...prev, companyCode: value }))
            }
            placeholder={intl.formatMessage({
              id: 'parts.entity.companyCode',
            })}
          />
        </Col>
      )}

      {showField(PartItemFieldsEnum.Description) && (
        <Col xs={24} md={12} xl={6}>
          <Input
            value={queryParams?.description}
            onChange={({ target: { value } }) =>
              setQueryParams((prev) => ({ ...prev, description: value }))
            }
            placeholder={intl.formatMessage({ id: 'parts.entity.description' })}
          />
        </Col>
      )}

      {showField(PartItemFieldsEnum.Group) && (
        <Col xs={24} md={12} xl={6}>
          <GroupsInfiniteSelectField
            allowClear
            value={queryParams?.subGroup}
            onChange={(value) =>
              setQueryParams((prev) => ({ ...prev, subGroup: value }))
            }
            placeholder={intl.formatMessage({ id: 'parts.entity.subGroup' })}
          />
        </Col>
      )}

      {matrixSettings?.matrixSettings.defaultPartApplication &&
        showVehicleFields && (
          <>
            <Col xs={24} md={12} xl={6}>
              <BrandsInfiniteSelect
                allowClear
                style={{ width: '100%' }}
                placeholder={intl.formatMessage({
                  id: 'searchParts.label.automaker',
                })}
                value={queryParams?.brandId}
                onChange={(value) => {
                  setBrandIdSelected(value);

                  setQueryParams((prev) => ({ ...prev, brandId: value }));
                }}
              />
            </Col>

            <Col xs={24} md={12} xl={6}>
              <ModelsInfiniteSelect
                allowClear
                style={{ width: '100%' }}
                placeholder={intl.formatMessage({
                  id: 'searchParts.label.model',
                })}
                queryFilter={{ brandId: brandIdSelected }}
                queryEnable={!!brandIdSelected && showVehicleFields}
                onChange={(value) =>
                  setQueryParams((prev) => ({ ...prev, modelId: value }))
                }
              />
            </Col>
          </>
        )}

      {!matrixSettings?.matrixSettings.defaultPartApplication && (
        <Col xs={24} md={12} xl={6}>
          <Input
            value={queryParams?.partApplicationDescription}
            onChange={({ target: { value } }) =>
              setQueryParams((prev) => ({
                ...prev,
                partApplicationDescription: value,
              }))
            }
            placeholder={intl.formatMessage({
              id: 'parts.entity.partApplicationDescription',
            })}
          />
        </Col>
      )}

      <Col xs={24} md={12} xl={12}>
        <Input
          value={queryParams?.observation}
          onChange={({ target: { value } }) =>
            setQueryParams((prev) => ({ ...prev, observation: value }))
          }
          placeholder={'Observação'}
        />
      </Col>

      <Col xs={24} md={12} xl={6}>
        <ActiveExtraFilter
          value={queryParams?.active}
          onChange={(value) =>
            setQueryParams((prev) => ({
              ...prev,
              active: value as unknown as boolean,
            }))
          }
        />
      </Col>

      <Col xs={24} md={12} xl={6}>
        <Input
          placeholder="Localização"
          onChange={({ target: { value } }) =>
            setQueryParams((prev) => ({
              ...prev,
              location: value,
            }))
          }
        />
      </Col>

      {showFilterPartReplacement && (
        <Col xs={24} md={12} xl={6}>
          <Checkbox
            checked={queryParams?.isPartReplacement}
            onChange={({ target: { checked } }) =>
              setQueryParams((prev) => ({
                ...prev,
                isPartReplacement: checked,
              }))
            }
            style={{ marginTop: 10 }}
          >
            Mostrar peça substituta ao pesquisar?
          </Checkbox>
        </Col>
      )}
    </Row>
  );
}
